// ABA "ROTAS" — o gestor confere o Roteiro da Semana de quem está na hierarquia dele.
//
// O PROBLEMA QUE ELA RESOLVE: até 14/09/2026 o roteiro semanal só existia dentro do aparelho de quem
// estava logado. O gestor não tinha como validar a rota do time — não havia tela nem dado.
//
// DOIS MODOS, e os dois são de primeira classe:
//  • PRÉVIA — o gestor roda o MESMO motor aqui no navegador, ANTES de a pessoa gerar. É o uso principal:
//    conferir se a rota da semana faz sentido e só então mandar o vendedor gerar. NÃO depende de o app
//    ter feito nada; funciona com qualquer pessoa da hierarquia, a qualquer momento.
//  • ESPELHO — depois que a pessoa gera no aplicativo, o app GRAVA o roteiro
//    (`roteirosSemanais/{uid}_{semana}`) e esta tela passa a mostrá-lo. É literalmente a mesma rota que
//    ela viu, com a partida e os parâmetros que ela usou — serve de confirmação do que foi conferido.
//
// A prévia NÃO é a rota final, e a tela diz isso: o motor parte de um ponto, e no aplicativo esse ponto
// é o GPS do vendedor no instante em que ele gerar. Por isso a partida aqui é escolhida no mapa pelo
// gestor, e nunca chutada.
//
// POR QUE A PARTIDA NÃO É AUTOMÁTICA: o motor descarta tudo além de 50 km do ponto de partida. Chutar
// a partida não erra "um pouco" — pode zerar a rota inteira (foi o caso do supervisor 915, carteira em
// SC/RS e gestor no ES). Um número inventado aqui viraria uma tela mentindo com confiança.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Polyline, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Client, User, RoteirizacaoSettings, DEFAULT_ROTEIRIZACAO,
  managedSellers, sellerDisplayName, getAccessScope, isFullAccess, isScopedManager,
} from '../../types';
import { fetchCarteiraDeVendedor, fetchClientesDoUsuario } from '../../services/clientsQuery';
import { LatLng, SmartStop } from '../../utils/smartRoute';
import {
  generateWeeklyRoute, montarOpts, ordenarCarteira, inicioDaSemana, RAIO_MAX_KM,
  DiaRoteiro, WeeklyRouteResult,
} from '../../utils/smartWeekRoute';
import 'leaflet/dist/leaflet.css';

const DAY_COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#4b5563'];
const MAX_OPCOES = 40; // itens listados no seletor por vez (acesso total enxerga a empresa inteira)
// Teto de pinos cinza da carteira no mapa. Sem ele, um gerente nacional (~16 mil clientes) desenharia
// 16 mil CircleMarker e o Leaflet trava a aba. O texto ao lado do mapa avisa quando o corte agiu.
const MAX_PINOS = 400;

// Firestore devolve Timestamp; o motor faz `new Date(c.lastCheckinAt)`, que num Timestamp dá Invalid
// Date — e aí TODO cliente vira "nunca visitado", ou seja, vencido há um ciclo. A rota sairia errada
// sem nenhum erro na tela. Mesmo helper que o LiveMapPage usa.
const toDateAny = (v: any): Date | null => (v?.toDate ? v.toDate() : v instanceof Date ? v : null);
const fmtDataHora = (v: any): string => {
  const d = toDateAny(v);
  return d ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
};
// Tolerante de propósito: `origin` vem de um documento gravado pelo aplicativo, e um doc antigo ou
// truncado não pode derrubar a tela inteira com "toFixed of undefined".
const coord = (p: any): string =>
  (typeof p?.lat === 'number' && typeof p?.lng === 'number') ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : '—';
const primeiroNome = (n?: string) => (n || '').trim().split(' ')[0] || 'vendedor';

// Forma comum ao espelho e à simulação — o documento salvo tem exatamente o shape do motor.
interface Roteiro {
  dias: DiaRoteiro[];
  resumo: WeeklyRouteResult['resumo'];
  rodizio: WeeklyRouteResult['rodizio'];
  distantes: { clientId: string; nome: string; municipio: string; distKm: number }[];
  distantesTotal: number;
  backlog: { clientId: string; nome: string; municipio: string; diasAtraso: number }[];
  backlogTotal: number;
  alertas: WeeklyRouteResult['alertas'];   // alimenta o KPI "Sem geo"
}

function FitBounds({ pts }: { pts: [number, number][] }) {
  const map = useMap();
  useEffect(() => { if (pts.length) { try { map.fitBounds(pts as any, { padding: [40, 40], maxZoom: 15 }); } catch { /* ignore */ } } }, [pts, map]);
  return null;
}

function CliqueDefinePartida({ onPick }: { onPick: (p: LatLng) => void }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

export default function RotasPage() {
  const { user } = useAuth();
  const [todos, setTodos] = useState<User[]>([]);
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const [alvo, setAlvo] = useState<User | null>(null);

  const [settings, setSettings] = useState<RoteirizacaoSettings>(DEFAULT_ROTEIRIZACAO);
  const [salvo, setSalvo] = useState<any | null>(null);
  const [buscandoSalvo, setBuscandoSalvo] = useState(false);

  // Simulação
  const [carteira, setCarteira] = useState<Client[] | null>(null);
  const [flags, setFlags] = useState<string[]>([]);
  const [preparando, setPreparando] = useState(false);
  const [gerando, setGerando] = useState(false);   // motor rodando (síncrono, trava a aba — ver simularDe)
  const [partida, setPartida] = useState<LatLng | null>(null);
  const [sim, setSim] = useState<WeeklyRouteResult | null>(null);
  const [erro, setErro] = useState('');
  // `aviso` != `erro`: aqui a tela FUNCIONOU, mas com dado incompleto — e numa tela de conferência isso
  // é mais perigoso que uma falha, porque o resultado parece completo.
  const [aviso, setAviso] = useState('');
  // Invalida cargas em voo quando o gestor troca de usuário (ver buscarSalvo).
  const alvoSeq = useRef(0);
  const timerBlur = useRef<number | undefined>(undefined);
  useEffect(() => () => { if (timerBlur.current) window.clearTimeout(timerBlur.current); }, []);

  const [selDay, setSelDay] = useState(0);

  const semana = inicioDaSemana(new Date());

  useEffect(() => {
    (async () => {
      try {
        const [us, st] = await Promise.all([
          getDocs(query(collection(db, 'users'), orderBy('name'))),
          getDoc(doc(db, 'settings', 'roteirizacao')),
        ]);
        setTodos(us.docs.map((d) => ({ id: d.id, ...d.data() })) as User[]);
        // Spread sobre o DEFAULT: o doc em produção é mais velho que o tipo e não tem os campos novos.
        if (st.exists()) setSettings({ ...DEFAULT_ROTEIRIZACAO, ...st.data() } as RoteirizacaoSettings);
      } catch (e: any) {
        setErro('Não foi possível carregar a lista de usuários. ' + (e?.message || ''));
      }
    })();
  }, []);

  // Hierarquia: quem este gestor pode ver. Mesma função da tela de Vendedores — acesso total vê todo
  // mundo de campo; gestor com escopo vê quem tem o CODVEND dele em qualquer um dos seis campos de
  // chefia. Só entram USUÁRIOS DO APP: quem não tem conta não gera roteiro e não teria o que mostrar.
  // Ordenado por CÓDIGO crescente, o mesmo critério (e o mesmo `?? 1e9`) que o DashboardPage usa — é
  // por código que o gestor procura alguém, não por nome. Sem isto a lista vinha em ordem alfabética,
  // herdada do `orderBy('name')` da consulta.
  // ⚠️ Isto DESFAZ o "o próprio gestor em 1º lugar" que o `managedSellers` monta. Lá o motivo era evitar
  // escolher a linha errada e mandar rota (e push) para outra pessoa; aqui nada é enviado — a tela só
  // lê e simula —, e o rótulo "(você)" continua marcando quem é quem.
  // Sem CODVEND vai para o FIM, e o empate desempata por nome.
  // ⚠️ Os dois casos precisam ser tratados à mão, e cada atalho erra um deles:
  //   • o `(x ?? 1e9)` cru do DashboardPage acerta null/undefined, mas devolve NaN se o campo vier como
  //     texto não-numérico — e um comparador que devolve NaN embaralha a lista sem erro nenhum;
  //   • trocar por `Number(x)` sozinho conserta aquilo e QUEBRA este: `Number(null)` é 0, `Number('')`
  //     também, então quem não tem código pularia para o TOPO como se fosse o vendedor 0.
  // O tipo promete `number`, mas o servidor trata o campo com `String(...)`/`Number(...)` em vários
  // pontos (`_gestoresEscopados`, `managedSellers`) — ou seja, não dá para confiar nele.
  const cod = (u: User) => {
    const v: any = u.codVendSankhya;
    if (v == null || v === '') return 1e9;
    const n = Number(v);
    return Number.isFinite(n) ? n : 1e9;
  };
  const opcoes = useMemo(() => managedSellers(user, todos)
    .filter((s) => (s as any).active !== false)
    .sort((a, b) => cod(a) - cod(b) || (a.name || '').localeCompare(b.name || '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, todos]);
  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const base = t ? opcoes.filter((s) => sellerDisplayName(s).toLowerCase().includes(t)) : opcoes;
    return base.slice(0, MAX_OPCOES);
  }, [opcoes, busca]);

  const escolher = (s: User) => {
    alvoSeq.current++; // invalida qualquer carga em voo do usuário anterior
    setAlvo(s); setAberto(false); setBusca('');
    setSalvo(null); setSim(null); setPartida(null); setCarteira(null); setFlags([]); setErro(''); setSelDay(0);
    buscarSalvo(s, alvoSeq.current);
  };

  // GUARDA DE SEQUÊNCIA — mesmo padrão do `loadSeq` do DashboardPage. Sem ela, trocar de usuário no
  // seletor antes de a leitura anterior voltar deixava o estado cruzado: a resposta de A chegava depois
  // da de B e o painel exibia o roteiro de A carimbado com o nome de B. Numa tela cujo propósito é o
  // gestor CONFERIR a rota de alguém, atribuir a rota errada à pessoa errada é o pior defeito possível.
  const buscarSalvo = async (s: User, seq: number) => {
    setBuscandoSalvo(true);
    try {
      const snap = await getDoc(doc(db, 'roteirosSemanais', `${s.id}_${semana}`));
      if (seq !== alvoSeq.current) return; // outro usuário foi escolhido no meio do caminho
      setSalvo(snap.exists() ? snap.data() : null);
    } catch (e: any) {
      if (seq !== alvoSeq.current) return;
      setSalvo(null);
      // O caso quase certo é permission-denied por DEFASAGEM, e vale nomeá-lo: o seletor monta a lista
      // com a hierarquia AO VIVO (`managedSellers`), mas a regra valida contra `equipeSellerIds`, um
      // instantâneo que o `sincronizarEscopoAgendado` reescreve de 2 em 2 horas. Um subordinado
      // recém-vinculado aparece aqui e ainda é recusado lá — sem esta frase, parece defeito da tela.
      const negado = String(e?.code || e?.message || '').includes('permission-denied');
      setErro(negado
        ? 'Sem permissão para ver o roteiro desta pessoa. Se o vínculo dela com você é recente, o escopo '
          + 'ainda não foi recalculado (roda de 2 em 2 horas) — tente mais tarde ou use "Sincronizar equipes" nas Configurações.'
        : 'Não foi possível ler o roteiro salvo. ' + (e?.message || ''));
    } finally {
      if (seq === alvoSeq.current) setBuscandoSalvo(false);
    }
  };

  // Carrega o que a simulação precisa. Só quando o gestor pede: a carteira de um gerente nacional é a
  // base inteira (~16 mil), e não vale baixá-la só porque alguém abriu a aba.
  const prepararSimulacao = async () => {
    if (!alvo) return;
    const seq = alvoSeq.current;             // mesma guarda do buscarSalvo: ver o comentário lá
    const quem = alvo;
    setPreparando(true); setErro(''); setAviso(''); setSim(null);
    const alertas: string[] = [];
    try {
      // A carteira do ALVO tem de ser a mesma que o app dele usaria: vendedor roteiriza a própria
      // carteira; gestor roteiriza a hierarquia inteira como um bloco (desde 29/08). O escopo sai do
      // doc do próprio alvo (`equipeCodvends`/`equipeSellerIds`), que já veio na lista de usuários.
      const ehGestor = isFullAccess(quem.role) || isScopedManager(quem.role);
      let brutos: any[];
      if (ehGestor) {
        brutos = await fetchClientesDoUsuario(quem, getAccessScope(quem));
      } else {
        const r = await fetchCarteiraDeVendedor(quem);
        brutos = r.clientes;
        // Consulta recusada = carteira incompleta. Precisa aparecer: a rota sai plausível e errada.
        if (r.negadas > 0) {
          alertas.push(`${r.negadas} das consultas à carteira foram recusadas — a lista pode estar incompleta, `
            + 'e então esta prévia não reflete a rota real. Costuma ser escopo defasado (recalculado de 2 em 2 horas).');
        }
      }
      if (seq !== alvoSeq.current) return;   // trocaram de usuário enquanto isto carregava
      const cs = brutos.map((c: any) => ({ ...c, lastCheckinAt: toDateAny(c.lastCheckinAt) })) as Client[];
      setCarteira(cs);

      // "Visitar depois": o motor FORÇA esses clientes na rota, ignorando estrela 0 e o raio de 50 km.
      // Uma falha de leitura aqui vira lista vazia e some com eles da prévia — silenciosamente, e
      // justamente com os clientes que alguém marcou à mão como prioritários. Por isso o aviso.
      try {
        const f = await getDoc(doc(db, 'roteiroFlags', quem.id));
        const arr = f.exists() ? ((f.data() as any)?.visitarDepois || []) : [];
        if (seq === alvoSeq.current) setFlags(Array.isArray(arr) ? arr.map(String) : []);
      } catch {
        if (seq === alvoSeq.current) setFlags([]);
        alertas.push('Não foi possível ler as marcas "visitar depois" desta pessoa. Se houver alguma, '
          + 'ela NÃO entrou nesta prévia — no aplicativo entraria.');
      }

      if (seq !== alvoSeq.current) return;
      if (cs.length === 0) setErro('Nenhum cliente encontrado na carteira desta pessoa.');
      if (alertas.length) setAviso(alertas.join(' '));
    } catch (e: any) {
      if (seq === alvoSeq.current) setErro('Não foi possível carregar a carteira. ' + (e?.message || ''));
    } finally {
      if (seq === alvoSeq.current) setPreparando(false);
    }
  };

  // Roda o motor. Disparado pelo CLIQUE no mapa — clicar de novo move a partida e regera.
  //
  // ⚠️ O motor é SÍNCRONO e pesado: k-means sobre a carteira inteira, que para um gerente nacional é a
  // base toda (~16 mil). Rodá-lo direto no handler do clique segura a thread da interface por mais de um
  // segundo, e como o React não chega a pintar nada antes, a aba simplesmente CONGELA — o gestor acha
  // que o clique não funcionou e clica de novo, enfileirando outra rodada. O `setTimeout(0)` cede um
  // quadro ao navegador para o "Gerando..." aparecer antes de a thread travar, e o `gerando` barra o
  // clique repetido.
  const simularDe = (p: LatLng) => {
    if (!alvo || !carteira || gerando) return;
    const seq = alvoSeq.current;
    const quem = alvo;
    const dados = carteira;
    setPartida(p);
    setGerando(true);
    setTimeout(() => {
      try {
        const carteiraOrd = ordenarCarteira(dados);
        const r = generateWeeklyRoute(carteiraOrd, settings, montarOpts(settings, {
          dataRef: new Date(), origin: p, flagged: flags, placa: (quem as any)?.placaVeiculo,
        }));
        if (seq !== alvoSeq.current) return;  // trocaram de usuário enquanto o motor rodava
        setSim(r);
        const primeiro = r.dias.findIndex((d) => d.rota.length > 0);
        setSelDay(primeiro >= 0 ? primeiro : 0);
      } catch (e: any) {
        if (seq === alvoSeq.current) setErro('O motor falhou: ' + (e?.message || ''));
      } finally {
        if (seq === alvoSeq.current) setGerando(false);
      }
    }, 0);
  };

  // O que está na tela: o espelho tem prioridade; a simulação só aparece quando o gestor a roda.
  const roteiro: Roteiro | null = sim
    ? { ...sim, distantesTotal: sim.distantes.length, backlogTotal: sim.backlog.length }
    : salvo
      ? {
        dias: salvo.dias || [], resumo: salvo.resumo, rodizio: salvo.rodizio,
        distantes: salvo.distantes || [], distantesTotal: salvo.distantesTotal ?? (salvo.distantes || []).length,
        backlog: salvo.backlog || [], backlogTotal: salvo.backlogTotal ?? (salvo.backlog || []).length,
        // Doc antigo/truncado pode não ter `alertas` — cair para 0 é melhor que quebrar o KPI.
        alertas: salvo.alertas || { semCoordenada: 0 },
      }
      : null;
  const ehSimulacao = !!sim;
  const origemMostrada: LatLng | null = sim ? partida : (salvo?.origin || null);

  // `selDay` LIMITADO ao que existe: `dias` muda de tamanho conforme `settings.diasRota`, e um índice
  // fora da faixa deixava a tela sem aba marcada e sem tabela, parecendo que a rota sumiu.
  const dias: DiaRoteiro[] = Array.isArray(roteiro?.dias) ? roteiro!.dias : [];
  const diaIdx = dias.length ? Math.min(selDay, dias.length - 1) : 0;
  const dia = dias[diaIdx];
  // Filtra parada sem coordenada numérica: o documento salvo vem do aplicativo e pode estar truncado ou
  // ser de uma versão antiga. Um `lat` indefinido vira NaN dentro do Leaflet e derruba o mapa inteiro —
  // e não há ErrorBoundary nesta página, então levaria a tela junto.
  const stops: SmartStop[] = useMemo(
    () => ((dia?.rota || []) as SmartStop[]).filter((s) => typeof s?.lat === 'number' && typeof s?.lng === 'number'),
    [dia]
  );
  const linha: [number, number][] = useMemo(() => {
    const pts: [number, number][] = [];
    if (origemMostrada) pts.push([origemMostrada.lat, origemMostrada.lng]);
    stops.forEach((s) => pts.push([s.lat, s.lng]));
    return pts;
  }, [origemMostrada, stops]);

  // Mapa da simulação ANTES do clique: mostra a carteira para o gestor saber onde clicar.
  const pinsCarteira: [number, number][] = useMemo(() => {
    if (!carteira) return [];
    return carteira
      .filter((c) => typeof c.location?.latitude === 'number' && typeof c.location?.longitude === 'number')
      .slice(0, MAX_PINOS)
      .map((c) => [c.location!.latitude, c.location!.longitude] as [number, number]);
  }, [carteira]);

  const modoEscolherPartida = !!carteira && !sim;
  const ptsParaEnquadrar = linha.length ? linha : pinsCarteira;
  const centro: [number, number] = ptsParaEnquadrar[0] || [-23.5505, -46.6333];

  return (
    <div>
      <div style={styles.head}>
        <div>
          <h1 style={styles.title}>Rotas</h1>
          <p style={styles.subtitle}>
            Gere e confira o <strong>Roteiro da Semana</strong> de quem está na sua hierarquia — inclusive
            de quem ainda <strong>não gerou</strong> o dele. Depois que a pessoa gera no aplicativo, aqui
            passa a aparecer <strong>exatamente</strong> o roteiro que ela viu.
          </p>
        </div>
      </div>

      {/* ---------- quem ---------- */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Usuário</h3>
        <div style={{ position: 'relative', maxWidth: 420 }}>
          <input
            style={styles.input}
            placeholder={alvo ? sellerDisplayName(alvo) : 'Buscar por código ou nome...'}
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setAberto(true); }}
            onFocus={() => setAberto(true)}
            // 150 ms antes de fechar: sem essa folga o blur derruba a lista antes de o clique na opção
            // chegar. O timer fica num ref e é limpo no unmount (ver o efeito de limpeza abaixo).
            onBlur={() => { timerBlur.current = window.setTimeout(() => setAberto(false), 150); }}
          />
          {aberto && filtradas.length > 0 && (
            <div style={styles.dropdown}>
              {filtradas.map((s) => (
                <div
                  key={s.id}
                  style={styles.opcao}
                  // `onMouseDown` + preventDefault: com `onClick` o blur do input fecha a lista antes
                  // de o clique chegar, e a escolha se perde.
                  onMouseDown={(e) => { e.preventDefault(); escolher(s); }}
                >
                  {sellerDisplayName(s)}{s.id === user?.id ? ' (você)' : ''}
                </div>
              ))}
            </div>
          )}
        </div>
        <p style={styles.aviso}>
          A lista traz apenas <strong>usuários do aplicativo</strong> abaixo de você. Vendedor da equipe
          do Sankhya que não tem conta no app não gera roteiro e por isso não aparece aqui.
        </p>
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}
      {aviso && <div style={styles.avisoBanner}>⚠️ {aviso}</div>}

      {/* ---------- estado ---------- */}
      {alvo && buscandoSalvo && <div style={styles.card}>Procurando o roteiro desta semana...</div>}

      {alvo && !buscandoSalvo && (
        <div style={styles.card}>
          {salvo && !sim && (
            <div style={styles.selo}>
              <strong>Roteiro do próprio {primeiroNome(alvo.name)}</strong> — gerado no aplicativo em{' '}
              {fmtDataHora(salvo.geradoEm)}, partindo de {coord(salvo.origin)}.
              É a mesma rota que ele vê na tela dele.
            </div>
          )}
          {ehSimulacao && (
            <div style={styles.seloSim}>
              <strong>Prévia</strong> — gerada por você agora, com o mesmo motor do aplicativo, partindo
              de {partida ? coord(partida) : '—'}. Quando {primeiroNome(alvo.name)} gerar no aplicativo,
              a partida será o <strong>GPS dele naquele momento</strong>, então a ordem das visitas pode
              sair diferente. Clique de novo no mapa para mover a partida e recalcular.
            </div>
          )}
          {!salvo && !sim && !carteira && (
            <div>
              <p style={{ margin: '0 0 10px', fontSize: 14, color: '#555' }}>
                Gere aqui o roteiro de <strong>{sellerDisplayName(alvo)}</strong> para conferir se a
                semana faz sentido antes de mandar essa pessoa gerar no aplicativo.
                {' '}(Ela ainda não gerou o dela nesta semana, a partir de{' '}
                {semana.split('-').reverse().join('/')}.)
              </p>
              <button style={styles.btn} onClick={prepararSimulacao} disabled={preparando}>
                {preparando ? 'Carregando carteira...' : 'Gerar roteiro da semana'}
              </button>
            </div>
          )}
          {salvo && !sim && (
            <button style={styles.btnSec} onClick={prepararSimulacao} disabled={preparando}>
              {preparando ? 'Carregando carteira...' : 'Gerar prévia com outra partida'}
            </button>
          )}
          {/* O motor trava a thread enquanto roda; sem esta linha o gestor não tem sinal nenhum de que o
              clique foi registrado e clica de novo. */}
          {gerando && (
            <p style={styles.gerando}>
              ⏳ Montando o roteiro{carteira ? ` sobre ${carteira.length} clientes` : ''}... a página pode
              ficar presa alguns segundos.
            </p>
          )}
          {modoEscolherPartida && !gerando && (
            <p style={styles.instrucao}>
              📍 <strong>Clique no mapa</strong> para definir de onde a pessoa sai — o roteiro é gerado
              na hora. O motor descarta tudo que estiver a mais de {RAIO_MAX_KM} km desse ponto, e é por
              isso que ele não é chutado automaticamente.
              {/* Honesto sobre o corte: o mapa desenha no máximo MAX_PINOS pontos, e anunciar o total da
                  carteira faria o gestor procurar pinos que não estão lá. */}
              {carteira ? (pinsCarteira.length < carteira.length
                ? ` Os pontos cinza são ${pinsCarteira.length} dos ${carteira.length} clientes da carteira (o mapa não desenha todos).`
                : ` Os pontos cinza são a carteira (${carteira.length} clientes).`) : ''}
            </p>
          )}
        </div>
      )}

      {/* ---------- mapa ---------- */}
      {(roteiro || modoEscolherPartida) && (
        <div style={styles.mapWrapper}>
          <MapContainer center={centro} zoom={11} style={{ height: 460, width: '100%' }}>
            <OsmTiles />
            <FitBounds pts={ptsParaEnquadrar} />
            {/* Com a carteira em mãos, clicar SEMPRE (re)simula — inclusive para mover a partida de uma
                simulação que já está na tela. Sem carteira (só o espelho), o clique não faz nada. */}
            {carteira && <CliqueDefinePartida onPick={simularDe} />}
            {modoEscolherPartida && pinsCarteira.map((p, i) => (
              <CircleMarker key={`c${i}`} center={p} radius={3} pathOptions={{ color: '#9ca3af', weight: 1, fillOpacity: 0.6 }} />
            ))}
            {linha.length > 1 && (
              <Polyline positions={linha} pathOptions={{ color: DAY_COLORS[diaIdx % DAY_COLORS.length], weight: 4, opacity: 0.85 }} />
            )}
            {origemMostrada && (
              <CircleMarker center={[origemMostrada.lat, origemMostrada.lng]} radius={9} pathOptions={{ color: '#111827', weight: 2, fillColor: '#fff', fillOpacity: 1 }}>
                <Tooltip permanent direction="top">Partida</Tooltip>
              </CircleMarker>
            )}
            {stops.map((s) => (
              <CircleMarker
                key={s.clientId}
                center={[s.lat, s.lng]}
                radius={9}
                pathOptions={{ color: '#fff', weight: 2, fillColor: DAY_COLORS[diaIdx % DAY_COLORS.length], fillOpacity: 1 }}
              >
                <Tooltip direction="top">{`${s.ordem}. ${s.nome} — ${s.chegada}`}</Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* ---------- resultado ---------- */}
      {roteiro && (
        <>
          {/* MESMOS cinco KPIs do app, nesta ordem e com estes rótulos. Não é preferência de estilo: o
              gestor compara a tela dele com o print que o vendedor manda, e nome diferente para o mesmo
              número faz os dois discutirem sobre dados que são iguais.
              ⚠️ "Elegíveis" NÃO é `resumo.roteirizaveis` — é ele MENOS os distantes, a mesma conta do
              `naRota` do `weekRouteView` do app (241 roteirizáveis − 16 distantes = 225 elegíveis).
              ⚠️ Usa os TOTAIS (`distantesTotal`/`backlogTotal`), nunca `array.length`: no espelho as
              listas vêm cortadas em 50 e o KPI mostraria 50 onde o app mostra 102.
              O "Km aproximado" saiu daqui porque o app também não o tem nos KPIs — ele aparece no resumo
              do dia, logo abaixo. */}
          <div style={styles.kpis}>
            <Kpi rotulo="Elegíveis" valor={String(Math.max(0, (roteiro.resumo?.roteirizaveis ?? 0) - roteiro.distantesTotal))} />
            <Kpi rotulo="Visitas" valor={String(roteiro.resumo?.visitas ?? 0)} />
            <Kpi rotulo="Não encaixados" valor={String(roteiro.backlogTotal)} />
            {/* SEPARADOS de propósito, igual ao app: "está longe" e "não tem coordenada" são problemas
                opostos, com soluções opostas (mudar a partida × geocodificar). */}
            <Kpi rotulo="Distantes" valor={String(roteiro.distantesTotal)} />
            <Kpi rotulo="Sem geo" valor={String(roteiro.alertas?.semCoordenada ?? 0)} />
          </div>

          {roteiro.rodizio?.aplicado && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Rodízio de São Paulo</h3>
              <p style={{ margin: 0, fontSize: 13.5, color: '#555' }}>
                {roteiro.rodizio.troca === 'trocada' && 'A regra remanejou a zona restrita para outro dia da semana.'}
                {roteiro.rodizio.troca === 'nao_precisou' && 'A regra rodou e não precisou mexer em nada.'}
                {roteiro.rodizio.troca === 'sem_zona_livre' && '⚠️ Não havia dia livre para onde remanejar — a zona restrita ficou VAZIA naquele dia.'}
                {roteiro.rodizio.troca === 'nao_aplicado' && 'A regra não chegou a agir.'}
                {(roteiro.rodizio.aRever?.length || 0) > 0 && ` ${roteiro.rodizio.aRever.length} cliente(s) a rever.`}
              </p>
            </div>
          )}

          <div style={styles.card}>
            <div style={styles.abas}>
              {dias.map((d, i) => (
                <button
                  key={i}
                  onClick={() => setSelDay(i)}
                  style={{
                    ...styles.aba,
                    ...(i === diaIdx ? { background: DAY_COLORS[i % DAY_COLORS.length], color: '#fff', borderColor: 'transparent' } : {}),
                  }}
                >
                  {/* Tolerante: o documento salvo vem do aplicativo e pode ser de uma versão antiga. */}
                  {String(d?.dia || `Dia ${i + 1}`).replace('-feira', '')} ({d?.rota?.length ?? 0})
                </button>
              ))}
            </div>

            {dia && stops.length > 0 ? (
              <>
                <p style={styles.resumoDia}>
                  {stops.length} visitas
                  {typeof dia.kmAprox === 'number' ? ` · ~${dia.kmAprox} km` : ''}
                  {dia.almoco?.inicio ? ` · almoço ${dia.almoco.inicio}–${dia.almoco.fim}` : ''}
                  {dia.encerramento ? ` · última saída ${dia.encerramento}` : ''}
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.tabela}>
                    <thead>
                      <tr>
                        <th style={styles.th}>#</th>
                        <th style={styles.th}>Horário</th>
                        <th style={styles.th}>Cliente</th>
                        <th style={styles.th}>Município / bairro</th>
                        <th style={styles.th}>★</th>
                        <th style={styles.th}>Atraso</th>
                        <th style={styles.th}>Deslocamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stops.map((s) => (
                        <tr key={s.clientId}>
                          <td style={styles.td}>{s.ordem}</td>
                          <td style={styles.td}>{s.chegada}–{s.saida}</td>
                          <td style={styles.td}>{s.codparc ? `${s.codparc} - ${s.nome}` : s.nome}</td>
                          <td style={styles.td}>{[s.municipio, s.bairro].filter(Boolean).join(' / ') || '—'}</td>
                          <td style={styles.td}>{s.estrelas}</td>
                          <td style={styles.td}>{s.diasAtraso > 0 ? `${s.diasAtraso} d` : '—'}</td>
                          <td style={styles.td}>{Math.round(s.deslocamentoMin)} min</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 14, color: '#888' }}>Nenhuma visita planejada neste dia.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div style={styles.kpi}>
      <div style={styles.kpiValor}>{valor}</div>
      <div style={styles.kpiRotulo}>{rotulo}</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 10, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14, maxWidth: 660 },
  card: { background: '#fff', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 12px', fontSize: 16, color: '#1a1a2e' },
  input: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
  opcao: { padding: '9px 12px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #f5f5f5' },
  aviso: { margin: '10px 0 0', fontSize: 12.5, color: '#666', lineHeight: 1.5, maxWidth: 680 },
  avisoBanner: { background: '#fff8e1', color: '#7a5b00', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13.5, lineHeight: 1.5 },
  erro: { background: '#fdecea', color: '#c62828', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13.5 },
  selo: { background: '#e8f5e9', color: '#1b5e20', borderRadius: 10, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 10 },
  seloSim: { background: '#fff4e5', color: '#8a4b00', borderRadius: 10, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 10 },
  instrucao: { margin: '10px 0 0', fontSize: 13.5, color: '#555', lineHeight: 1.5 },
  gerando: { margin: '10px 0 0', fontSize: 13.5, color: '#8a4b00', fontWeight: 600, lineHeight: 1.5 },
  btn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' },
  btnSec: { background: '#fff', color: '#f5821f', border: '1px solid #f5821f', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13.5 },
  mapWrapper: { borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.08)', marginBottom: 14 },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 14 },
  kpi: { background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  kpiValor: { fontSize: 22, fontWeight: 700, color: '#1a1a2e' },
  kpiRotulo: { fontSize: 12, color: '#777', marginTop: 2 },
  abas: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  aba: { border: '1px solid #e0e0e0', background: '#fafafa', borderRadius: 999, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#444' },
  resumoDia: { margin: '0 0 10px', fontSize: 13, color: '#666' },
  tabela: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f5f5f5', color: '#222' },
};
